import * as uuid from "uuid";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import React, { FC, useCallback, useEffect, useState } from "react";
import { GridToolbarContainer } from "@material-ui/data-grid";
import { useDispatch, useSelector } from "react-redux";

import AdGroupSelectionPanel from "../../../../../views/redesign/AdGroupSelectionPanel";
import BaseDataTable from "../../../../../components/modules/BaseDataTable";
import Button from "../../../../../components/elements/Button";
import CreativeAssetsManagement from "./CreativeAssetsManagement";
import LandingPageEditMode from "./LandingPageEditMode";
import LandingPageViewMode from "./LandingPageViewMode";
import PrimaryButton from "../../../../../components/redesign/elements/Button/PrimaryButton";
import UploadCreativesModal from "./UploadCreativesModal";
import UploadFromTemplateModal from "./UploadFromTemplateModal";
import api from "../../../../../api";
import tableClasses from "./AdGroupsCreativesDashboard.module.scss";
import { AdGroup, AdGroupType, CreativeAsset } from "../../../../../types/adGroupTypes";
import { AdLibCreativeReportType, BrandgenCampaignPool, ReportTimeFrames } from "../../../../../types/campaignTypes";
import { ReduxApplicationState } from "../../../../../types/ReduxTypes/ReduxApplicationState";
import {
  createAdGroup,
  setAdGroupToCreate,
  uploadCreativeAssets,
} from "../../../../../redux/reducers/campaignToCreate";
import { errorToaster, successToaster } from "../../../../../helpers/toasters";
import { truncateText } from "../../../../../helpers/truncateText";
import { useCampaignDashboardStyles } from "../../CampaignDashboardStylesContext";

const useNewDesign = process.env.REACT_APP_FEATURE_NEW_DESIGN === "true";

type AdGroupsCreativesMap = Record<string, CreativeAsset[]>;
type CreativeReportsMap = Record<string, AdLibCreativeReportType[]>;

type AdgroupsDashboardProps = {
  selectedCampaign: BrandgenCampaignPool;
  selectedTimeFrame: ReportTimeFrames;
  customReportDate: Date | undefined;
  isCustomDateChanged: boolean;
};

const fetchData = async (adGroupId: string, date?: Date) => {
  try {
    if (date) {
      const { payload } = await api.getAdGroupWithDailyCreativeReports(adGroupId, date);
      return payload;
    }
    const { payload } = await api.getAdGroupWithCreativeReports(adGroupId);
    return payload;
  } catch (error) {
    throw new Error((error as any).message);
  }
};

const fetchAdGroup = async (brandgenCampaignPoolId: string) => {
  try {
    const { payload } = await api.getAdGroupByCampaignId(brandgenCampaignPoolId);
    return payload;
  } catch (error) {
    throw new Error((error as any).message);
  }
};

const AdGroupsCreativesDashboard: FC<AdgroupsDashboardProps> = ({
  selectedCampaign,
  selectedTimeFrame,
  customReportDate,
  isCustomDateChanged,
}) => {
  const classes = useCampaignDashboardStyles();
  const dispatch = useDispatch();
  const adGroupToCreate = useSelector((state: ReduxApplicationState) => state.campaignToCreate.adGroupToCreate);
  const currentUser = useSelector((state: ReduxApplicationState) => state.user.currentUser);
  const [adGroupsCreativesMap, setAdGroupsCreativesMap] = useState<AdGroupsCreativesMap>({});
  const [adGroup, setAdGroup] = useState<AdGroup | undefined>(undefined);
  const [creativeReportsMap, setCreativeReportsMap] = useState<CreativeReportsMap>({});
  const [isFetchingCreatives, setIsFetchingCreatives] = useState<boolean>(false);
  const [isAdGroupsPending, setIsAdGroupsPending] = useState<boolean>(false);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [isOpenModalTemplates, setIsOpenModalTemplates] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [selectedAdGroupIdInModal, setSelectedAdGroupIdInModal] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [landingPage, setLandingPage] = useState<string>("");
  const [fetchedAdGroupId, setFetchedAdGroupId] = useState<string>("");

  const columns = [
    { key: "addNewCreatives", label: "", width: "22%", customStyles: { whiteSpace: "nowrap" } },
    {
      key: "name",
      label: "Name",
      width: "48%",
      customStyles: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    },
    { key: "createdDate", label: "Created Date", width: "18%", align: "left" as const },
  ] as const;

  const resetStates = () => {
    setFetchedAdGroupId("");
    setAdGroup(undefined);
    setIsAdGroupsPending(false);
  };

  const fetchAndSetAdGroup = useCallback(async () => {
    try {
      setIsAdGroupsPending(true);
      const fetchedAdGroup = await fetchAdGroup(selectedCampaign.brandgenCampaignPoolId);

      if (!fetchedAdGroup) {
        resetStates();
        return;
      }

      setFetchedAdGroupId(fetchedAdGroup.adGroupId);
      setAdGroup(fetchedAdGroup);
    } catch (error_) {
      const error = error_ as any;
      errorToaster(`There was an error: ${error.message}`);
    } finally {
      setIsAdGroupsPending(false);
    }
  }, [selectedCampaign]);

  const isCustomDate = selectedTimeFrame === ReportTimeFrames.CUSTOM_DATE;

  const fetchAndSetCreatives = useCallback(async () => {
    try {
      if (!fetchedAdGroupId) {
        resetStates();
        return;
      }

      setIsFetchingCreatives(true);

      const response = await fetchData(
        fetchedAdGroupId,
        isCustomDate && customReportDate && isCustomDateChanged ? customReportDate : undefined,
      );

      // eslint-disable-next-line consistent-return
      if (!response) return resetStates();

      const { creativesWithAdGroup, creativesReports } = response;

      setAdGroupsCreativesMap({ [fetchedAdGroupId]: creativesWithAdGroup.creatives });
      setCreativeReportsMap({ [fetchedAdGroupId]: creativesReports });

      setLandingPage(creativesWithAdGroup.creatives?.[0]?.click_url || "");
    } catch (error_) {
      const error = error_ as any;
      errorToaster(`There was an error 1: ${error.message}`);
    } finally {
      setIsFetchingCreatives(false);
    }
  }, [fetchedAdGroupId, isCustomDate, customReportDate, isCustomDateChanged]);

  const handleSaveAdGroup = async () => {
    if (!adGroupToCreate || adGroupToCreate.creativeAssets.length === 0) {
      errorToaster("Please select an ad group with creative assets.", 5000);
      return;
    }

    try {
      setIsSaving(true);

      const newAdGroupId = `bg-ad-group-${uuid.v4()}`;
      const createdAdGroup = await createAdGroup({
        adGroupId: newAdGroupId,
        brandgenAdvertiserId: selectedCampaign.brandgenAdvertiserId,
        brandgenCampaignPoolId: selectedCampaign.brandgenCampaignPoolId,
        createdBy: currentUser,
        name: adGroupToCreate.name,
        adGroupType: adGroupToCreate.adGroupType as AdGroupType,
        description: adGroupToCreate.description ?? "",
      });

      if (!createdAdGroup) throw new Error("Failed to create AdGroup.");

      await uploadCreativeAssets(
        adGroupToCreate.creativeAssets,
        selectedCampaign.adLibAdvertiserId,
        selectedCampaign.brandgenAdvertiserId,
        landingPage,
        createdAdGroup.adGroupId,
        api.changeCreative,
        selectedCampaign.brandgenCampaignPoolId,
      );

      await api.deactivateOldCreatives(fetchedAdGroupId);
      await api.deleteAdGroup(fetchedAdGroupId);
      await Promise.all(
        (adGroupsCreativesMap[fetchedAdGroupId] ?? []).map((asset) =>
          api.deleteCreative({
            bgCreativeId: asset.bgCreativeId,
            beeswaxCreativeId: asset.beeswaxCreativeId,
          }),
        ),
      );

      await fetchAndSetAdGroup();

      dispatch(setAdGroupToCreate({ name: "", description: "", adGroupType: AdGroupType.IMAGE, creativeAssets: [] }));
      successToaster("Creatives successfully replaced.", 2000);
      setIsOpenModal(false);
    } catch (error_) {
      const error = error_ as any;
      errorToaster(`There was an error while uploading new Ad Group: ${error.message}`, 5000);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (selectedCampaign.brandgenCampaignPoolId) {
      fetchAndSetAdGroup();
    }
  }, [selectedCampaign.brandgenCampaignPoolId, fetchAndSetAdGroup]);

  useEffect(() => {
    if (fetchedAdGroupId) {
      fetchAndSetCreatives();
    }
  }, [fetchedAdGroupId, fetchAndSetCreatives]);

  useEffect(() => {
    if (adGroup && adGroupsCreativesMap[adGroup.adGroupId]?.[0]?.creativeType === "video/mp4") {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [adGroupsCreativesMap, adGroup]);

  const rows = [
    adGroup
      ? {
          id: adGroup.adGroupId,
          name: (
            <Tooltip title={adGroup.name}>
              <Typography className={tableClasses.nameCell} component="span">
                {truncateText(adGroup.name, 40)}
              </Typography>
            </Tooltip>
          ),
          createdDate: (
            <Typography className={tableClasses.dateCell} component="span">
              {new Date(adGroup.createdAt).toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </Typography>
          ),
          addNewCreatives: (
            <div className={tableClasses.actionCell}>
              {useNewDesign ? (
                <PrimaryButton onClick={() => setIsOpenModal(true)} disabled={disabled}>
                  Upload New AdGroup
                </PrimaryButton>
              ) : (
                <Button onClick={() => setIsOpenModal(true)} variant="contained" disabled={disabled}>
                  Upload New AdGroup
                </Button>
              )}
            </div>
          ),
          collapsableConfigs: {
            collapsable: true,
            collapsableChild: (
              <CreativeAssetsManagement
                isFetchingCreatives={isFetchingCreatives}
                creativeAssets={adGroupsCreativesMap[adGroup.adGroupId] ?? []}
                creativeDailyReports={creativeReportsMap[adGroup.adGroupId] ?? []}
                selectedTimeFrame={selectedTimeFrame}
              />
            ),
            expandControlHandler: () => fetchAndSetCreatives(),
          },
        }
      : {
          id: "",
          name: "",
          createdDate: "",
          addNewCreatives: "",
        },
  ];

  return (
    <div className={classes.dashboardContainer}>
      <GridToolbarContainer className={classes.toolbar}>
        <Typography variant="h3">Campaign Creative List</Typography>
        {isEditMode ? (
          <LandingPageEditMode
            landingPage={landingPage}
            changeMode={() => setIsEditMode(false)}
            adGroupId={adGroup?.adGroupId}
            fetchAndSetCreatives={fetchAndSetCreatives}
          />
        ) : (
          <LandingPageViewMode landingPage={landingPage} changeMode={() => setIsEditMode(true)} />
        )}
      </GridToolbarContainer>

      {isAdGroupsPending ? (
        <Typography className={classes.loaderLabel}>Please, wait while we get everything in order...</Typography>
      ) : adGroup === undefined ? (
        <Typography className={classes.noAdGroupsText}>
          Unfortunately there is no information about Ad Groups...
        </Typography>
      ) : (
        <div className={tableClasses.tableSection}>
          <BaseDataTable columns={columns} rows={rows} compact />
        </div>
      )}

      {useNewDesign ? (
        <Dialog
          open={isOpenModal}
          onClose={() => {
            if (!isSaving) {
              setIsOpenModal(false);
              setSelectedAdGroupIdInModal(null);
            }
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Upload new AdGroup</DialogTitle>
          <DialogContent>
            <AdGroupSelectionPanel onSelectionChange={(id) => setSelectedAdGroupIdInModal(id)} />
          </DialogContent>
          <DialogActions>
            <PrimaryButton disabled={!selectedAdGroupIdInModal || isSaving} onClick={handleSaveAdGroup}>
              {isSaving ? "Saving…" : "Save"}
            </PrimaryButton>
          </DialogActions>
        </Dialog>
      ) : (
        <UploadCreativesModal
          isOpen={isOpenModal}
          handleClose={() => setIsOpenModal(false)}
          adLibAdvertiserId={selectedCampaign.adLibAdvertiserId}
          brandgenAdvertiserId={selectedCampaign.brandgenAdvertiserId}
          landingPage={landingPage}
          fetchedAdGroupId={fetchedAdGroupId}
          fetchAndSetAdGroup={fetchAndSetAdGroup}
          brandgenCampaignId={selectedCampaign.brandgenCampaignPoolId}
          fetchedCreativesAssets={adGroupsCreativesMap[fetchedAdGroupId] ?? []}
        />
      )}
      <UploadFromTemplateModal
        isOpen={isOpenModalTemplates}
        handleClose={() => setIsOpenModalTemplates(false)}
        adLibAdvertiserId={selectedCampaign.adLibAdvertiserId}
        brandgenAdvertiserId={selectedCampaign.brandgenAdvertiserId}
        landingPage={landingPage}
        fetchedAdGroupId={fetchedAdGroupId}
        fetchAndSetAdGroup={fetchAndSetAdGroup}
        brandgenCampaignId={selectedCampaign.brandgenCampaignPoolId}
        fetchedCreativesAssets={adGroupsCreativesMap[fetchedAdGroupId] ?? []}
      />
    </div>
  );
};

export default AdGroupsCreativesDashboard;
